import { useCallback, useState } from "react"

import { mergeArrays, toggle } from "shared/lib/arrayUtils"
import { useEvent } from "shared/lib/useEvent"
import { TreeNode } from "shared/types/TreeNode"
import { WithID } from "shared/types/WithID"

import { FlattenedTableItemType, OnTableItemSelect } from "../types"

interface UseTableSelectArgs<I extends WithID> {
  flattenedTree: TreeNode<FlattenedTableItemType<I>>[]
  selectedItems: string[]
  multiSelect?: boolean
  additiveSelect?: boolean
  onSelectedItemsChange?: (ids: string[]) => void
}

export function useTableSelect<I extends WithID>({
  flattenedTree,
  selectedItems,
  multiSelect,
  additiveSelect,
  onSelectedItemsChange
}: UseTableSelectArgs<I>) {
  const [lastSelectedItemID, setLastSelectedItemID] = useState<string>()
  const [selectedItemsSnapshot, setSelectedItemsSnapshot] = useState<string[]>()

  const isItemSelected = useCallback(
    (item: TreeNode<I>) => !!selectedItems?.includes(item.id),
    [selectedItems]
  )

  const handleSelect: OnTableItemSelect = useEvent((id, index, options) => {
    const isSelected = selectedItems.includes(id)

    if (multiSelect && options?.isRange && lastSelectedItemID) {
      const prevIndex = flattenedTree.findIndex(
        item => item.id === lastSelectedItemID
      )

      const startIndex = prevIndex < index ? prevIndex : index
      const endIndex = prevIndex >= index ? prevIndex : index

      const itemsToSelect = flattenedTree
        .slice(startIndex, endIndex + 1)
        .map(item => item.id)

      setSelectedItemsSnapshot(selectedItems)
      if (selectedItemsSnapshot) {
        onSelectedItemsChange?.(
          mergeArrays(selectedItemsSnapshot, itemsToSelect)
        )
      } else {
        onSelectedItemsChange?.(mergeArrays(selectedItems, itemsToSelect))
      }

      return
    }

    if (multiSelect && additiveSelect && !options?.isRange) {
      setSelectedItemsSnapshot(undefined)
      setLastSelectedItemID(!isSelected ? id : undefined)
      onSelectedItemsChange?.(toggle(id, selectedItems))
      return
    }

    setSelectedItemsSnapshot(undefined)
    setLastSelectedItemID(!isSelected ? id : undefined)

    if (multiSelect && (options?.isMulti || options?.isRange)) {
      onSelectedItemsChange?.(toggle(id, selectedItems))
      return
    }

    if (selectedItems.length > 1) {
      setLastSelectedItemID(id)
      onSelectedItemsChange?.([id])
      return
    }

    onSelectedItemsChange?.([id])
  })

  const handleMultiSelect = useEvent((ids: string[]) => {
    setLastSelectedItemID(undefined)
    setSelectedItemsSnapshot(ids)
    onSelectedItemsChange?.(ids)
  })

  return {
    isItemSelected,
    onSelect: handleSelect,
    onMultiSelect: handleMultiSelect
  }
}
